import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import moment from "moment-timezone";
import Dialog from "../../../../../components/shared/Dialog";
import Button from "../../../../../components/shared/Button";
import { useActionsMutations } from "../../api/useActionsMutations";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";

function Actions({ visible, close, values }: any) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { submitMutation, submitUserMutation, bulkSubmitMutation } = useActionsMutations({ values, close });

    const message = useMemo(() => {
        if (values?.type === "cell") {
            return t("do-you-want-to-submit-event", {
                action: values?.action
            });
        } else if (values?.type === "row") {
            return t("do-you-want-to-submit-user-event", {
                action: values?.action
            });
        } else if (values?.type === "day" || values?.type === "bulk-day") {
            return t("do-you-want-to-submit-day-event", {
                action: values?.action,
                day: moment(values?.day).format(dateFormat)
            });
        } else {
            return t("do-you-want-to-submit-events", {
                action: values?.action,
            });
        }
    }, [values?.type, values?.action, values?.day, dateFormat, t]);

    const loading = useMemo(() => {
        if (values?.type === "cell") {
            return submitMutation.isPending;
        } else if (values?.type === "row") {
            return submitUserMutation.isPending;
        } else {
            return bulkSubmitMutation.isPending;
        }
    }, [
        values?.type,
        submitMutation.isPending,
        submitUserMutation.isPending,
        bulkSubmitMutation.isPending
    ]);

    const onConfirm = useCallback(() => {
        if (values?.type === "cell") {
            submitMutation.mutate();
        } else if (values?.type === "row") {
            submitUserMutation.mutate();
        } else {
            bulkSubmitMutation.mutate();
        }
    }, [values?.type, bulkSubmitMutation, submitMutation, submitUserMutation]);

    return (
        <Dialog open={visible} close={close} size="lg">
            <div className="tw-flex tw-flex-col tw-items-center tw-my-3">
                <CheckCircle2 size={50} className="tw-text-primary tw-mb-3" />
                <h2 className="tw-text-center tw-font-bold tw-mb-3">
                    {t("are-you-sure")}
                </h2>

                <div className="tw-text-sm tw-mb-7">
                    {message}
                </div>

                <div className="tw-flex tw-items-center tw-gap-2 tw-justify-center">
                    <Button type="button" variant="ghost" onClick={close} disabled={loading}>
                        {t("cancel")}
                    </Button>

                    <Button type="button" onClick={onConfirm} loading={loading}>
                        {t("confirm")}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

export default Actions;
